import type { RaRecord } from "react-admin";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import { formatListDate, formatWeekdayFull } from "@/utils/date-format";
import {
  resolveBookingHallLineVenue,
  resolveRazaEventVenueFromRecord,
} from "@/utils/raza-event-venue-display";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ReceiptHeader from "@/components/receipt-layout/ReceiptHeader";
import { RazaPrintCommitmentsBlock } from "@/components/raza/RazaPrintCommitmentsBlock";
import "./raza-unified-print.css";
import { useHardcopyBorders } from "@/theme/useHardcopyBorders";
import { useRazaPrintPaperSize } from "@/hooks/useRazaPrintPaperSize";
import { fromGregorian } from "@/utils/hijri-date-utils";
import { getRazaPrintLayout } from "@/utils/raza-print-paper-size";

type BookingHallLine = {
  id: string;
  date?: string | Date;
  slot?: string;
  thaals?: number;
  hallName?: string | null;
  /** Hydrated booking lines from `bookings` resource use nested `hall`. */
  hall?: { name?: string | null; venue?: string | null };
};

type RazaEventPrintRow = {
  id: string;
  date?: string | Date;
  startTime?: string;
  title?: string | null;
  purpose?: string | null;
  purposeName?: string | null;
  address?: string | null;
  notes?: string | null;
  hallBookings?: Array<{
    id: string;
    date?: string | Date;
    slot?: string;
    hallName?: string | null;
    hall?: { name?: string | null; venue?: string | null };
  }>;
};

export type RazaUnifiedPrintRecord = RaRecord & {
  itsNo?: string;
  name?: string;
  mohallah?: string | null;
  /** ITS `Jamaat` when present, else stored mohallah (server-computed). */
  displayMohalla?: string | null;
  /** ITS `Mobile` when present, else hall booking phone / first event `mobileNo` (server-computed). */
  displayMobile?: string | null;
  purpose?: string;
  purposeName?: string | null;
  requestDate?: string;
  booking?: {
    id?: string;
    organiser?: string | null;
    phone?: string | null;
    mohalla?: string | null;
    sadarat?: string | null;
    hallBookings?: BookingHallLine[];
  } | null;
  razaEvents?: RazaEventPrintRow[];
};

function TableCellComp({
  children,
  fontSizePx,
}: {
  children: React.ReactNode;
  fontSizePx: number;
}) {
  const theme = useTheme();
  return (
    <TableCell
      sx={{
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSizePx,
        textTransform: "capitalize",
      }}
    >
      {children}
    </TableCell>
  );
}

function ScheduleTableHead({
  fourthColumnLabel,
  fontSizePx,
}: {
  fourthColumnLabel: string;
  fontSizePx: number;
}) {
  return (
    <TableHead>
      <TableRow>
        <TableCellComp fontSizePx={fontSizePx}>Venue</TableCellComp>
        <TableCellComp fontSizePx={fontSizePx}>Date</TableCellComp>
        <TableCellComp fontSizePx={fontSizePx}>Day</TableCellComp>
        <TableCellComp fontSizePx={fontSizePx}>{fourthColumnLabel}</TableCellComp>
        <TableCellComp fontSizePx={fontSizePx}>Time</TableCellComp>
      </TableRow>
    </TableHead>
  );
}

function formatThaalsCell(thaals: number | null | undefined) {
  if (thaals == null || Number.isNaN(Number(thaals))) return "—";
  if (thaals === 0) return "تقسیم";
  return thaals;
}

function formatDateCell(dateRaw: string | Date | undefined) {
  if (!dateRaw) return { greg: "", hijri: "", day: "" };
  const d = typeof dateRaw === "string" ? new Date(dateRaw) : dateRaw;
  if (Number.isNaN(d.getTime())) return { greg: "", hijri: "", day: "" };
  return {
    greg: formatListDate(d),
    hijri: fromGregorian(d),
    day: formatWeekdayFull(d),
  };
}

export function RazaUnifiedPrintContent({
  record,
  includeCommitments = false,
}: {
  record: RazaUnifiedPrintRecord;
  includeCommitments?: boolean;
}) {
  const theme = useTheme();
  const { solid1Soft, solid5, solid1 } = useHardcopyBorders();
  const paperSize = useRazaPrintPaperSize();
  const layout = getRazaPrintLayout(paperSize);

  const reqDate = record.requestDate ? new Date(record.requestDate) : null;
  const reqDateValid = reqDate && !Number.isNaN(reqDate.getTime());

  const purposeLabel =
    typeof record.purposeName === "string" && record.purposeName.trim()
      ? record.purposeName
      : typeof record.purpose === "string"
        ? record.purpose
        : "";

  const booking = record.booking ?? null;
  const bookingHallLines = Array.isArray(booking?.hallBookings) ? booking?.hallBookings : [];
  const showHallSchedule = bookingHallLines?.length && bookingHallLines?.length > 0;

  const events = Array.isArray(record.razaEvents) ? record.razaEvents : [];
  const showEventsSchedule = !showHallSchedule && events.length > 0;

  const mohallaForPrint =
    (typeof record.displayMohalla === "string" && record.displayMohalla.trim()) ||
    (typeof record.mohallah === "string" && record.mohallah.trim()) ||
    "";

  const mobileForPrint =
    (typeof record.displayMobile === "string" && record.displayMobile.trim()) ||
    (typeof booking?.phone === "string" && booking.phone.trim()) ||
    "";

  const content = (
    <Box
      p={layout.padding}
      className={layout.rootClassName}
      sx={{ fontFamily: theme.typography.fontFamily }}
    >
      <ReceiptHeader />

      <Box sx={{ borderTop: solid5, p: layout.padding }}>
        <Box display="flex" justifyContent="space-between" mb={layout.fieldMb}>
          ITS No. :
          <Box flex={3} sx={{ borderBottom: solid1Soft, mr: 5, ml: 1 }}>
            {record.itsNo}
          </Box>
          <Box flex={3} sx={{ borderBottom: solid1Soft, textAlign: "right", mr: 1 }}>
            {reqDateValid ? formatListDate(reqDate) : ""}
          </Box>
          : تاريخ
        </Box>

        <Box display="flex" justifyContent="space-between" mb={layout.fieldMb}>
          <>Name :</>
          <Box flex={3} sx={{ borderBottom: solid1Soft, ml: 1 }}>
            {record.name}
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-start" mb={layout.fieldMb} alignItems="flex-start">
          <>Mohalla :</>
          <Box flex={1} sx={{ borderBottom: solid1Soft, ml: 1, minHeight: 24 }}>
            {mohallaForPrint}
          </Box>
        </Box>

        {mobileForPrint ? (
          <Box display="flex" justifyContent="space-between" mb={layout.fieldMb}>
            <>Mobile No :</>
            <Box flex={3} sx={{ borderBottom: solid1Soft, ml: 1 }}>
              {mobileForPrint}
            </Box>
          </Box>
        ) : null}

        {booking?.organiser &&
        booking.organiser.trim() &&
        booking.organiser.trim() !== String(record.name ?? "").trim() ? (
          <Box display="flex" justifyContent="space-between" mb={layout.fieldMb}>
            <>Hall organiser :</>
            <Box flex={3} sx={{ borderBottom: solid1Soft, ml: 1 }}>
              {booking.organiser}
            </Box>
          </Box>
        ) : null}

        <Box textAlign="right" mb={layout.fieldMb}>
          <>بعد التحيات جناب عامل صاحب</>
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={layout.fieldMb}>
          <Box sx={{ borderBottom: solid1Soft, mr: 2 }}>{record.name}</Box>
          <>: الاخ النجيب / الاخت النجيبه</>
        </Box>

        <Box display="flex" justifyContent="flex-end" mb={layout.fieldMb}>
          <>ني رزا واسطے حاضر تھیا چھے</>
          <Box sx={{ borderBottom: solid1Soft, ml: 2 }}>{purposeLabel}</Box>
        </Box>

        {showHallSchedule ? (
          <Box mt={layout.scheduleMt}>
            <Paper variant="outlined">
              <Table size="small">
                <ScheduleTableHead fourthColumnLabel="Thaals" fontSizePx={layout.cellFontSizePx} />
                <TableBody>
                  {bookingHallLines.map((hall) => {
                    const { greg, hijri, day } = formatDateCell(hall.date);
                    const venue = resolveBookingHallLineVenue(hall);
                    return (
                      <TableRow key={hall.id}>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>{venue}</TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {greg}
                          {hijri ? (
                            <>
                              <br />
                              {hijri}
                            </>
                          ) : null}
                        </TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>{day}</TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {formatThaalsCell(hall.thaals)}
                        </TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {hall.slot ?? ""}
                        </TableCellComp>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        ) : null}

        {showEventsSchedule ? (
          <Box mt={layout.scheduleMt}>
            <Paper variant="outlined">
              <Table size="small">
                <ScheduleTableHead fourthColumnLabel="Purpose" fontSizePx={layout.cellFontSizePx} />
                <TableBody>
                  {events.map((ev) => {
                    const { greg, hijri, day } = formatDateCell(ev.date);
                    const purposeEv =
                      typeof ev.purposeName === "string" && ev.purposeName.trim()
                        ? ev.purposeName
                        : (ev.purpose ?? "");
                    const halls = ev.hallBookings ?? [];
                    const linkedHallNames = halls
                      .map((h) => h.hallName ?? h.hall?.name ?? "")
                      .filter(Boolean);
                    const resolvedVenue = resolveRazaEventVenueFromRecord({
                      address: ev.address,
                      hallBookings: halls,
                    });
                    const primaryVenue = resolvedVenue.value;
                    const showLinkedHallsCaption =
                      linkedHallNames.length > 0 && resolvedVenue.source !== "hall";
                    const slotFromHalls = halls
                      .map((h) => (typeof h.slot === "string" ? h.slot : ""))
                      .filter(Boolean);
                    const startRaw = ev.startTime?.trim();
                    const start12 = startRaw ? formatRazaEventStartTimeLabel(startRaw) : "";
                    const startPart = start12 && start12 !== "—" ? start12 : "";
                    const timeDisplay =
                      [startPart, ...slotFromHalls.map((s) => s.trim()).filter(Boolean)].join(
                        " · "
                      ) || "—";

                    return (
                      <TableRow key={ev.id}>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {ev.title?.trim() ? (
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ fontWeight: 600, fontSize: layout.cellFontSizePx }}
                              >
                                {ev.title}
                              </Typography>
                              <br />
                            </>
                          ) : null}
                          {primaryVenue || "—"}
                          {showLinkedHallsCaption ? (
                            <Typography
                              variant="caption"
                              component="div"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Linked halls: {linkedHallNames.join(", ")}
                            </Typography>
                          ) : null}
                          {ev.notes?.trim() ? (
                            <Typography
                              variant="caption"
                              component="div"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {ev.notes}
                            </Typography>
                          ) : null}
                        </TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {greg}
                          {hijri ? (
                            <>
                              <br />
                              {hijri}
                            </>
                          ) : null}
                        </TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {day || "—"}
                        </TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {purposeEv || "—"}
                        </TableCellComp>
                        <TableCellComp fontSizePx={layout.cellFontSizePx}>
                          {timeDisplay}
                        </TableCellComp>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        ) : null}

        {includeCommitments ? (
          <RazaPrintCommitmentsBlock
            itsNo={record.itsNo}
            cellFontSizePx={layout.cellFontSizePx}
            mt={layout.scheduleMt}
          />
        ) : null}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
          gap={layout.paperSize === "A5" ? 2 : 4}
          mt={layout.fieldMb}
        >
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            sx={{ minHeight: layout.signatureMinHeightPx }}
          >
            <Box sx={{ borderTop: solid1, pt: layout.padding, width: "100%" }}>
              Jamaat Authority Signature
            </Box>
          </Box>

          <Box
            width={layout.stampSizePx}
            height={layout.stampSizePx}
            sx={{ border: solid1 }}
            display="flex"
            alignItems="flex-end"
            justifyContent="center"
          >
            <>عامل صاحب</>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {layout.paperSize === "A5_ROTATED" ? (
        <style>{`@media print { @page { size: A4 portrait; margin: 0; } }`}</style>
      ) : layout.paperSize === "A5" ? (
        <style>{`@media print { @page { size: A5; margin: 8mm; } }`}</style>
      ) : (
        <style>{`@media print { @page { size: A4; margin: 10mm; } }`}</style>
      )}
      {layout.isRotated ? (
        <Box className="raza-unified-print-rotation-wrapper">{content}</Box>
      ) : (
        content
      )}
    </>
  );
}
